import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useMobile, useVibration } from '../hooks/useMobile';

const { FiMic, FiMicOff, FiVolume2, FiVolumeX, FiPlay, FiPause } = FiIcons;

const VoiceCoding = ({ onInsertCode, onCommand }) => {
  const { isMobile } = useMobile();
  const { vibrateTouch, vibrateSuccess, vibrateError } = useVibration();
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [confidence, setConfidence] = useState(0);
  const [error, setError] = useState('');
  const recognitionRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Check if Speech Recognition is supported
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setIsSupported(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        console.log('🎤 Voice recognition started');
        setIsListening(true);
        setError('');
        vibrateTouch();
      };

      recognition.onend = () => {
        console.log('🎤 Voice recognition ended');
        setIsListening(false);
      };

      recognition.onerror = (event) => {
        console.error('🎤 Voice recognition error:', event.error);
        setError(`Speech recognition error: ${event.error}`);
        setIsListening(false);
        vibrateError();
      };

      recognition.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          const confidence = event.results[i][0].confidence;

          if (event.results[i].isFinal) {
            finalTranscript += transcript;
            setConfidence(confidence);
          } else {
            interimTranscript += transcript;
          }
        }

        setTranscript(finalTranscript || interimTranscript);

        if (finalTranscript) {
          handleVoiceCommand(finalTranscript.trim());
          vibrateSuccess();
        }
      };

      recognitionRef.current = recognition;
    } else {
      setError('Speech recognition not supported in this browser');
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleVoiceCommand = (command) => {
    const lowerCommand = command.toLowerCase();

    // Code insertion commands
    if (lowerCommand.includes('insert') || lowerCommand.includes('add')) {
      if (lowerCommand.includes('function')) {
        onInsertCode?.('function() {\n  \n}');
      } else if (lowerCommand.includes('console log')) {
        onInsertCode?.('console.log();');
      } else if (lowerCommand.includes('const')) {
        onInsertCode?.('const  = ');
      } else if (lowerCommand.includes('let')) {
        onInsertCode?.('let  = ');
      } else if (lowerCommand.includes('div')) {
        onInsertCode?.('<div></div>');
      } else if (lowerCommand.includes('comment')) {
        onInsertCode?.('// ');
      }
    }
    // Navigation commands
    else if (lowerCommand.includes('save')) {
      onCommand?.('save');
    } else if (lowerCommand.includes('preview')) {
      onCommand?.('preview');
    } else if (lowerCommand.includes('new file')) {
      onCommand?.('newFile');
    } else if (lowerCommand.includes('settings')) {
      onCommand?.('settings');
    }
    // If no specific command found, insert as text
    else {
      onInsertCode?.(command);
    }
  };

  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      setTranscript('');
      setError('');
      recognitionRef.current.start();

      // Auto-stop after 10 seconds
      timeoutRef.current = setTimeout(() => {
        stopListening();
      }, 10000);
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  };

  const toggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  if (!isSupported) {
    return null; // Don't show the component if not supported
  }

  return (
    <div className="voice-coding flex flex-col items-start">
      {/* Voice Control Button */}
      <motion.button
        onClick={toggleListening}
        disabled={!isSupported}
        className={`p-3 rounded-full transition-all duration-200 ${
          isListening
            ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg'
            : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
        whilePressed={{ scale: 0.95 }}
        whileTap={{ scale: 0.9 }}
        title={isListening ? 'Stop voice input' : 'Start voice input'}
      >
        <SafeIcon
          icon={isListening ? FiMicOff : FiMic}
          className={`text-xl ${isListening ? 'animate-pulse' : ''}`}
        />
      </motion.button>

      {/* Voice Input Status - FIXED positioning to show ABOVE button */}
      <AnimatePresence>
        {isListening && (
          <motion.div
            className="bg-gray-800 border border-gray-700 rounded-lg p-3 shadow-xl z-50 mb-2 w-80 max-w-[calc(100vw-2rem)]"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            style={{ 
              position: 'absolute',
              bottom: '100%', // Position above the button
              left: '0',
              marginBottom: '8px' // Small gap between status and button
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-white flex items-center">
                <SafeIcon icon={FiMic} className="mr-2 text-red-400 animate-pulse" />
                Listening...
              </span>
              <button onClick={stopListening} className="text-gray-400 hover:text-white">
                <SafeIcon icon={FiPause} />
              </button>
            </div>

            {transcript && (
              <div className="mb-2">
                <div className="text-xs text-gray-400 mb-1">Transcript:</div>
                <div className="text-sm text-green-300 bg-gray-900 rounded p-2">
                  {transcript}
                </div>
                {confidence > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    Confidence: {Math.round(confidence * 100)}%
                  </div>
                )}
              </div>
            )}

            <div className="text-xs text-gray-400">
              <div className="mb-1">
                <strong>Voice Commands:</strong>
              </div>
              <div>"insert function" • "console log" • "save" • "preview"</div>
              <div>"new file" • "add div" • "insert comment"</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Display - FIXED positioning to show ABOVE button */}
      <AnimatePresence>
        {error && (
          <motion.div
            className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 text-red-300 text-xs mb-2 w-80 max-w-[calc(100vw-2rem)]"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            style={{ 
              position: 'absolute',
              bottom: '100%', // Position above the button
              left: '0',
              marginBottom: '8px' // Small gap between error and button
            }}
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VoiceCoding;